import { useState, useEffect } from 'react'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Checkbox } from '@/components/ui/checkbox'
import { Alert, AlertDescription } from '@/components/ui/alert'
import { Loader2, CalendarIcon, Info } from 'lucide-react'

/**
 * Extracts date portion (YYYY-MM-DD) from ISO string without timezone conversion.
 */
function extractDatePart(isoString: string): string {
  if (/^\d{4}-\d{2}-\d{2}$/.test(isoString)) return isoString
  return isoString.split('T')[0]
}

/**
 * Formats a date string (YYYY-MM-DD) to pt-BR locale format (DD/MM/YYYY)
 */
function formatDateBR(dateString: string): string {
  const datePart = extractDatePart(dateString)
  const [year, month, day] = datePart.split('-')
  return `${day}/${month}/${year}`
}

/**
 * Compares two date strings (YYYY-MM-DD) without timezone issues.
 * Returns: -1 if a < b, 0 if a === b, 1 if a > b
 */
function compareDates(a: string, b: string): number {
  const dateA = extractDatePart(a)
  const dateB = extractDatePart(b)
  if (dateA < dateB) return -1
  if (dateA > dateB) return 1
  return 0
}

interface ImportFormProps {
  onSubmit: (data: { startDate: string; endDate: string; importConversations: boolean; importUsers: boolean }) => void
  existingPeriods?: Array<{ startDate: string; endDate: string }>
  isSubmitting?: boolean
  initialStartDate?: string
  initialEndDate?: string
}

export function ImportForm({
  onSubmit,
  existingPeriods = [],
  isSubmitting,
  initialStartDate = '',
  initialEndDate = '',
}: ImportFormProps) {
  const [startDate, setStartDate] = useState(initialStartDate)
  const [endDate, setEndDate] = useState(initialEndDate)
  const [importConversations, setImportConversations] = useState(true)
  const [importUsers, setImportUsers] = useState(true)

  // Update form when initial values change (e.g., when duplicating)
  useEffect(() => {
    setStartDate(initialStartDate)
    setEndDate(initialEndDate)
  }, [initialStartDate, initialEndDate])

  const hasOverlap = existingPeriods.some((period) => {
    if (!startDate || !endDate || !importConversations) return false
    // Compare dates as strings (YYYY-MM-DD format) to avoid timezone issues
    const periodStartStr = extractDatePart(period.startDate)
    const periodEndStr = extractDatePart(period.endDate)
    // Overlap: start <= periodEnd AND end >= periodStart
    return compareDates(startDate, periodEndStr) <= 0 && compareDates(endDate, periodStartStr) >= 0
  })

  // Validation: at least one option selected + dates required if importing conversations
  const isValid =
    (importConversations || importUsers) &&
    (!importConversations || (startDate && endDate && compareDates(startDate, endDate) <= 0))

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    if (!isValid) return

    // Only include dates if importing conversations
    const payload = {
      importConversations,
      importUsers,
      ...(importConversations && { startDate, endDate }),
    }

    onSubmit(payload as { startDate: string; endDate: string; importConversations: boolean; importUsers: boolean })
  }

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <div className="space-y-4">
        {/* Import Options */}
        <div className="space-y-3 rounded-lg border p-4 bg-muted/30">
          <div className="space-y-0.5">
            <Label className="text-sm font-medium">Opções de Importação</Label>
            <p className="text-xs text-muted-foreground">Selecione o que deseja importar</p>
          </div>

          <div className="space-y-4">
            {/* Import Conversations Option */}
            <div className="space-y-3">
              <div className="flex items-start space-x-3">
                <Checkbox
                  id="import-conversations"
                  checked={importConversations}
                  onCheckedChange={(checked) => setImportConversations(checked === true)}
                  disabled={isSubmitting}
                  className="mt-0.5"
                />
                <div className="space-y-0.5 flex-1">
                  <Label
                    htmlFor="import-conversations"
                    className="text-sm font-normal cursor-pointer"
                  >
                    Importar conversas
                  </Label>
                  <p className="text-xs text-muted-foreground">
                    Tickets fechados, protocolos e mensagens do período
                  </p>
                </div>
              </div>

              {/* Date fields - shown only when importing conversations */}
              {importConversations && (
                <div className="ml-7 space-y-3">
                  <div className="space-y-2">
                    <Label htmlFor="import-start-date" className="text-xs">
                      Data de Início
                    </Label>
                    <div className="relative">
                      <Input
                        id="import-start-date"
                        type="date"
                        value={startDate}
                        onChange={(e) => setStartDate(e.target.value)}
                        disabled={isSubmitting}
                        required={importConversations}
                        className="pl-10 h-9 text-sm"
                      />
                      <CalendarIcon className="absolute left-3 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-muted-foreground pointer-events-none" />
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="import-end-date" className="text-xs">
                      Data de Fim
                    </Label>
                    <div className="relative">
                      <Input
                        id="import-end-date"
                        type="date"
                        value={endDate}
                        onChange={(e) => setEndDate(e.target.value)}
                        disabled={isSubmitting}
                        required={importConversations}
                        className="pl-10 h-9 text-sm"
                      />
                      <CalendarIcon className="absolute left-3 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-muted-foreground pointer-events-none" />
                    </div>
                  </div>
                </div>
              )}
            </div>

            {/* Import Users Option */}
            <div className="flex items-start space-x-3">
              <Checkbox
                id="import-users"
                checked={importUsers}
                onCheckedChange={(checked) => setImportUsers(checked === true)}
                disabled={isSubmitting}
                className="mt-0.5"
              />
              <div className="space-y-0.5 flex-1">
                <Label
                  htmlFor="import-users"
                  className="text-sm font-normal cursor-pointer"
                >
                  Importar usuários
                </Label>
                <p className="text-xs text-muted-foreground">
                  Sincronizar todos os usuários cadastrados no CRM
                </p>
              </div>
            </div>
          </div>

          {!importConversations && !importUsers && (
            <Alert variant="destructive" className="mt-3">
              <AlertDescription className="text-xs">
                Selecione pelo menos uma opção de importação
              </AlertDescription>
            </Alert>
          )}
        </div>

        {hasOverlap && importConversations && (
          <Alert variant="default" className="border-blue-500">
            <Info className="h-4 w-4 text-blue-500" />
            <AlertDescription>
              <p className="font-semibold mb-2">Período já importado:</p>
              <ul className="list-disc list-inside space-y-1 text-sm mb-2">
                {existingPeriods
                  .filter((period) => {
                    const periodStartStr = extractDatePart(period.startDate)
                    const periodEndStr = extractDatePart(period.endDate)
                    return (
                      compareDates(startDate, periodEndStr) <= 0 &&
                      compareDates(endDate, periodStartStr) >= 0
                    )
                  })
                  .map((period, i) => (
                    <li key={i}>
                      {formatDateBR(period.startDate)} até {formatDateBR(period.endDate)}
                    </li>
                  ))}
              </ul>
              <p className="text-sm text-muted-foreground">
                Os dados existentes serão atualizados automaticamente durante a importação.
              </p>
            </AlertDescription>
          </Alert>
        )}

        <Alert>
          <AlertDescription className="text-sm">
            {importConversations && importUsers && 'A importação processará tickets do período selecionado e sincronizará todos os usuários do CRM. '}
            {importConversations && !importUsers && 'A importação processará apenas tickets do período selecionado. '}
            {!importConversations && importUsers && 'A importação sincronizará todos os usuários cadastrados no CRM. '}
            O processo é assíncrono e você pode acompanhar o progresso.
          </AlertDescription>
        </Alert>
      </div>

      <div className="flex justify-end gap-2 pt-4 border-t">
        <Button type="submit" disabled={!isValid || isSubmitting} className="w-full sm:w-auto">
          {isSubmitting && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
          Iniciar Importação
        </Button>
      </div>
    </form>
  )
}
